<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-select
          v-model="form.exam_year_batch"
          :items="examResultBatches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Exam Batch"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name}${
                item.specialization ? ` - ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.grade"
          :items="grades"
          outlined
          dense
          hide-details="auto"
          label="Grade"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.elective_status"
          :items="electiveStatus"
          outlined
          dense
          hide-details="auto"
          label="Elective Status"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="form.course_type"
          :items="courseTypes"
          outlined
          dense
          hide-details="auto"
          label="Course Type"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model.trim="form.course_code"
          label="Course Code"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="form.term_no"
          label="Subject Semester"
          type="number"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="form.published"
          :items="[
            { name: 'Yes', id: true },
            { name: 'No', id: false },
          ]"
          outlined
          dense
          hide-details="auto"
          label="Published"
          clearable
          item-text="name"
          item-value="id"
        />
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text mr-2"
          :loading="loading"
          @click="getExamResults(true)"
        >
          Search
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :disabled="!examResults.length"
          :loading="downloading"
          @click="download"
        >
          <v-icon left>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :disabled="!examResults.length"
          @click="openPublishDialog"
        >
          Publish
        </v-btn>
        <v-btn class="accent white--text mr-2" @click="uploadDialog = true">
          <v-icon left>mdi-upload</v-icon>
          SL Numbers
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── Table ────────────────────────────────────────────────────────── -->
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="examResults"
            :server-items-length="totalRecords"
            :options.sync="tableOptions"
            :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
            fixed-header
            class="secondary row-height-50"
            :loading="loading"
            @update:options="onTableOptionsChange"
          >
            <template #item.usn_id="{ item }">
              <span
                class="usn-link primary--text"
                @click="onUSNClick(item.usn_id)"
              >
                {{ item.usn_id }}
              </span>
            </template>

            <template #item.final_internal_mark="{ item }">
              {{ item.final_internal_mark ?? "-" }}
            </template>

            <template #item.final_external_mark="{ item }">
              {{ item.final_external_mark ?? "-" }}
            </template>

            <template #item.total="{ item }">
              {{ item.total ?? "-" }}
            </template>

            <!-- Annual programmes: grade is stored for reference only, never shown. -->
            <template #item.grade="{ item }">
              {{ item.is_annual ? "-" : item.grade ?? "-" }}
            </template>

            <template #item.credits="{ item }">
              {{ item.credits ?? "-" }}
            </template>

            <template #item.sgpa="{ item }">
              {{ item.sgpa ?? "-" }}
            </template>

            <template #item.cgpa="{ item }">
              {{ item.cgpa ?? "-" }}
            </template>

            <template #item.result="{ item }">
              <v-chip x-small :color="resultColor(item.result)" dark>
                {{ item.result }}
              </v-chip>
            </template>

            <template #no-data>
              <div class="text-center grey--text py-4">
                No results found. Apply filters and click Search.
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="publishDialog" max-width="480" persistent>
      <v-card>
        <v-form ref="publishFormRef" v-model="publishFormValid">
          <v-card-title class="font-weight-medium secondary--text primary"
            >Publish Exam Results <v-spacer></v-spacer>
            <v-btn small icon @click="publishDialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn></v-card-title
          >
          <v-card-text class="pt-4">
            <v-autocomplete
              v-model="publishForm.exam_year_batch"
              :items="examResultBatches"
              outlined
              dense
              hide-details="auto"
              label="Exam Batch*"
              :rules="[(v) => !!v || 'Exam Batch is required']"
              class="mb-4"
            />
            <v-autocomplete
              v-model="publishForm.program_id"
              :items="programs"
              :item-text="
                (item) =>
                  `${item.degree.short_name}${
                    item.specialization ? ` - ${item.specialization}` : ''
                  }`
              "
              item-value="id"
              outlined
              dense
              clearable
              hide-details="auto"
              label="Programme (optional)"
              class="mb-4"
            />

            <v-radio-group
              v-model="publishForm.action"
              row
              hide-details
              class="mb-2"
            >
              <v-radio label="Publish" value="publish" />
              <v-radio label="Unpublish" value="unpublish" />
            </v-radio-group>

            <v-divider class="my-3" />

            <div class="text-caption grey--text text--darken-1 mb-2">
              Optionally target specific USNs within the above scope
            </div>
            <v-radio-group
              v-model="publishForm.usn_mode"
              row
              hide-details
              class="mb-2"
            >
              <v-radio label="No USN filter" value="" />
              <v-radio label="Exclude these USNs" value="exclude" />
              <v-radio label="Only these USNs" value="only" />
            </v-radio-group>
            <v-combobox
              v-if="publishForm.usn_mode"
              v-model="publishForm.usns"
              multiple
              chips
              deletable-chips
              small-chips
              outlined
              dense
              hide-details="auto"
              label="Type a USN and press Enter"
            />
          </v-card-text>
        </v-form>
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn
            color="primary"
            :loading="publishLoading"
            @click="confirmationDialog = true"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <confirmationDialogForm
        v-if="confirmationDialog"
        :confirmationText="publishConfirmText()"
        @closeDialog="confirmationDialog = false"
        :loading="loading"
        @saveDialog="confirmPublish"
      />
    </v-dialog>
    <v-dialog v-model="uploadDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          SL Numbers CSV <v-spacer></v-spacer>
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn></v-card-title
        >
        <v-card-text class="pt-4">
          <v-file-input
            v-model="uploadFile"
            accept=".csv"
            label="Select CSV"
            outlined
            dense
            show-size
            hide-details="auto"
          >
            <template v-slot:selection="{ text }">
              <span class="text-body-2 text-truncate" style="max-width: 100%">
                {{ uploadFile ? uploadFile.name : text }}
              </span>
            </template>
          </v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary"
            :loading="uploadLoading"
            :disabled="!uploadFile"
            @click="confirmUpload"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { examResultHeaders } from "@/constants/headers";
import confirmationDialogForm from "@/components/confirmationDialog.vue";

export default {
  name: "StudentExamResults",
  components: { confirmationDialogForm },

  data() {
    return {
      form: {},
      loading: false,
      downloading: false,

      programs: [],
      examResultBatches: [],
      examResults: [],

      grades: ["S", "A+", "A", "B", "C", "D", "E", "F", "AB"],
      courseTypes: ["Theory", "Lab", "ST&SP", "ST&SL", "Project"],
      electiveStatus: ["Core", "Elective"],
      resultOptions: ["Pass", "Fail", "Absent", "Detained"],

      totalRecords: 0,
      tableOptions: { page: 1, itemsPerPage: 10 },
      isResetting: false,
      batches: [],
      headers: examResultHeaders,
      publishDialog: false,
      publishFormValid: false,
      publishLoading: false,
      publishForm: { exam_year_batch: null, program_id: null },
      confirmationDialog: false,
      uploadDialog: false,
      uploadFile: null,
      uploadLoading: false,
    };
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Exam Result" }]);
    this.init();
  },

  methods: {
    resultColor(result) {
      return (
        {
          Pass: "success",
          Fail: "error",
          Absent: "warning",
          Detained: "grey darken-1",
        }[result] ?? "grey"
      );
    },

    onUSNClick(usn) {
      this.form.usn_id = usn;
      this.getExamResults(true);
    },

    // Expands year_batch ("2024-Nov") → exam_year + exam_month params,
    // strips empty / null values.
    objectToFilteredParams(obj) {
      const newObj = { ...obj };
      if (newObj.exam_year_batch) {
        const [year, month] = newObj.exam_year_batch.split("-");
        newObj.exam_year = year;
        newObj.exam_month = month;
        delete newObj.exam_year_batch;
      }
      if (newObj.year_batch) {
        const [year, batch] = newObj.year_batch.split("-");
        newObj.year = year;
        newObj.batch = batch;
        delete newObj.year_batch;
      }
      return Object.keys(newObj)
        .filter(
          (key) =>
            newObj[key] !== undefined &&
            newObj[key] !== null &&
            newObj[key] !== "",
        )
        .reduce((acc, key) => {
          acc[key] = newObj[key];
          return acc;
        }, {});
    },

    async init() {
      await Promise.all([
        this.generateProgrammes(),
        this.getExamBatches(),
        this.generateBatches(),
      ]);
    },

    onTableOptionsChange(options) {
      if (this.isResetting) return;
      this.tableOptions = options;
      this.getExamResults();
    },

    async getExamResults(resetPage = false) {
      if (resetPage) {
        this.isResetting = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        this.$nextTick(() => {
          this.isResetting = false;
        });
      }

      this.loading = true;
      const params = this.objectToFilteredParams({ ...this.form });

      try {
        const res = await this.$axios.get("/exam_results", {
          params: {
            ...params,
            page: this.tableOptions.page,
            per_page: this.tableOptions.itemsPerPage,
          },
        });
        this.examResults = res.data.data.records;
        this.totalRecords = res.data.data.total;
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Failed to load results",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async generateProgrammes() {
      try {
        const res = await this.$axios.get("/programs");
        this.programs = res.data.data;
      } catch (err) {
        console.error(err);
      }
    },
    async generateBatches() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getExamBatches() {
      try {
        const res = await this.$axios.get("/exam_external_batches");
        this.examResultBatches = res.data.data;
      } catch (err) {
        console.error(err);
      }
    },

    async download() {
      const params = this.objectToFilteredParams({ ...this.form });
      this.downloading = true;

      this.$axios
        .get("/download_exam_results", { responseType: "blob", params })
        .then((res) => {
          const url = window.URL.createObjectURL(
            new Blob([res.data], { type: "text/csv" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Exam Results.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Download failed",
            color: "error",
          });
        })
        .finally(() => {
          this.downloading = false;
        });
    },
    openPublishDialog() {
      this.publishForm = {
        exam_year_batch: null,
        program_id: null,
        year_batch: null,
        action: "publish",
        usn_mode: "",
        usns: [],
      };
      this.publishDialog = true;
    },

    closePublishDialog() {
      this.publishForm = {
        exam_year_batch: null,
        program_id: null,
        year_batch: null,
        action: "publish",
        usn_mode: "",
        usns: [],
      };
      this.publishDialog = false;
      this.confirmationDialog = false;
      this.$refs.publishFormRef?.resetValidation();
    },

    publishConfirmText() {
      const verb =
        this.publishForm.action === "unpublish" ? "unpublish" : "publish";
      const scope =
        this.publishForm.usn_mode === "only"
          ? "only the specified USNs"
          : this.publishForm.usn_mode === "exclude"
          ? "all matching students except the specified USNs"
          : "all matching students";
      return `This will ${verb} results for ${scope}. This cannot be reverted once saved. Continue?`;
    },

    async confirmPublish() {
      if (!this.$refs.publishFormRef.validate()) return;

      const payload = this.objectToFilteredParams({ ...this.publishForm });
      if (!payload.usn_mode || !payload.usns?.length) {
        delete payload.usn_mode;
        delete payload.usns;
      }

      this.publishLoading = true;
      try {
        const res = await this.$axios.post("/exam_results/publish", payload);
        this.$store.commit("showSnackbar", {
          message: res.data.message || "Updated successfully",
          color: "success",
        });
        this.closePublishDialog();
        this.getExamResults();
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Publish failed",
          color: "error",
        });
      } finally {
        this.confirmationDialog = false;
        this.publishLoading = false;
      }
    },
    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadFile = null;
    },

    async confirmUpload() {
      if (!this.uploadFile) return;

      const formData = new FormData();
      formData.append("csv_file", this.uploadFile, this.uploadFile.name);

      this.uploadLoading = true;
      try {
        await this.$axios.post("/exam_results/upload_sl_numbers", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.$store.commit("showSnackbar", {
          message: "SL numbers updated successfully",
          color: "success",
        });
        this.closeUploadDialog();
        this.getExamResults();
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Upload failed",
          color: "error",
        });
      } finally {
        this.uploadLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.usn-link {
  text-decoration: underline;
  cursor: pointer;
}
.usn-link:hover {
  opacity: 0.8;
}
</style>
